import { Head, Link, useForm } from "@inertiajs/react";
import { useState } from "react";
import {
	type Conflict,
	ConflictDialog,
	findConflicts,
	mergeValues,
} from "@/components/conflict-dialog";
import type { Country } from "@/components/country-select";
import Dashboard from "@/components/dashboard";
import {
	type EditingPerson,
	EditingPersonItem,
} from "@/components/editing-person-item";
import {
	PERSON_FIELD_LABELS,
	type PersonFormData,
	PersonFormFields,
} from "@/components/person-form-fields";
import { Button } from "@/components/ui/button";
import type HomeUser from "@/types/serializers/HomeUser";

interface Props {
	user: HomeUser;
	countries: Country[];
}

interface FormData {
	physical_person: PersonFormData;
}

export default function NewIndividual({ user, countries }: Props) {
	const [editingPerson, setEditingPerson] = useState<EditingPerson>();
	const [pending, setPending] = useState<{
		conflicts: Conflict[];
		mergedValues: PersonFormData;
		description: string;
	}>();

	const { data, setData, post, processing, errors, clearErrors, transform } =
		useForm<FormData>({
			physical_person: {
				first_name: "",
				last_name: "",
				birth_number: "",
				street: null,
				city: null,
				postal_code: null,
				country: "CZ",
				document_type: null,
				document_number: null,
				document_expiry_date: null,
				document_issuer: null,
				birth_date: null,
				birth_place: null,
				birth_country: "CZ",
				citizenship: null,
				citizenship_other: null,
				gender: null,
				is_sanctioned: null,
				is_pep: null,
				pep_info: null,
			},
		});

	const defaults: Partial<PersonFormData> = { country: "CZ" };

	const applyOrShowConflicts = (
		values: PersonFormData,
		description: string,
	) => {
		const merged = mergeValues(data.physical_person, values);
		const conflicts = findConflicts(
			data.physical_person,
			values,
			PERSON_FIELD_LABELS,
			defaults,
		);
		if (conflicts.length === 0) {
			setData("physical_person", merged);
		} else {
			setPending({
				conflicts,
				mergedValues: merged,
				description,
			});
		}
	};

	const handleApply = () => {
		if (pending) setData("physical_person", pending.mergedValues);
		setPending(undefined);
	};

	const handleCancel = () => {
		setPending(undefined);
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		transform((data) => ({
			physical_person: {
				...data.physical_person,
				id: editingPerson?.id,
			},
		}));
		post("/clients/create_individual");
	};

	return (
		<>
			<Head title="Nový klient - Fyzická osoba" />
			<Dashboard user={user}>
				<form
					onSubmit={handleSubmit}
					className="flex flex-1 flex-col overflow-hidden"
				>
					<div className="overflow-y-auto p-4 lg:p-6">
						<div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
							<div>
								<h2 className="font-semibold text-2xl">
									Nový klient – Fyzická osoba
								</h2>
								<p className="text-muted-foreground text-sm">
									Vyplňte údaje o novém klientovi. Povinná pole jsou označena{" "}
									<span className="text-destructive">*</span>.
								</p>
							</div>

							{editingPerson && (
								<EditingPersonItem
									person={editingPerson}
									onCancel={() => setEditingPerson(undefined)}
								/>
							)}

							<PersonFormFields
								value={data.physical_person}
								onChange={(person) => {
									setData("physical_person", person);
									clearErrors();
								}}
								errors={errors}
								countries={countries}
								onPersonSelected={(person) => {
									setEditingPerson(person);
									applyOrShowConflicts(
										person.formData,
										`Vybrali jste existující osobu ${person.formData.first_name} ${person.formData.last_name}.`,
									);
								}}
							/>

							{pending && (
								<ConflictDialog
									conflicts={pending.conflicts}
									description={pending.description}
									onApply={handleApply}
									onCancel={handleCancel}
								/>
							)}
						</div>
					</div>

					<div className="border-t bg-background px-4 py-4 lg:px-6">
						<div className="mx-auto flex w-full max-w-2xl">
							<div className="ml-auto flex gap-4">
								<Button
									type="button"
									variant="outline"
									size="lg"
									render={<Link href="/clients" />}
								>
									Zrušit
								</Button>
								{editingPerson ? (
									<Button type="submit" size="lg" disabled={processing}>
										Uložit a přidat
									</Button>
								) : (
									<Button type="submit" size="lg" disabled={processing}>
										Vytvořit klienta
									</Button>
								)}
							</div>
						</div>
					</div>
				</form>
			</Dashboard>
		</>
	);
}
